import React, { useEffect, useRef, useState } from "react";
import { fetchAxiosNoToken } from "../../../helpers/fetchAxiosNoToken";
import { t } from "framer-motion/dist/types.d-B_QPEvFK";

interface FacturacionForm {
    tipo_comprobante: string;
    correo: string;
    correo_copia?: string;
    direccion: string;
    ubigeo: string;
    nombre_comercial: string;
    nombre_legal: string;
    tipo_documento_identidad: string;
    numero_documento_identidad: string;
}

type Props = {
    tipo_pedido: "Habitación" | "Casa";
    idReserva: string | number;
    tipoMoneda: string;
    amount: number | string;
    tipoPago: "Pago Completo" | "Pago Parcial";
    id_reserva?: string | number;
    isComprobante: boolean;
    factData?: FacturacionForm | null;
    renderKey: string;
    onReset?: () => void;
};

const SANDBOX_BASE =
    "https://static-content-qas.vnforapps.com/env/sandbox/js/checkout.js";

export const NiubizPayButton: React.FC<Props> = ({
    tipo_pedido,
    idReserva,
    tipoMoneda,
    amount,
    tipoPago,
    id_reserva,
    isComprobante,
    factData,
    renderKey,
    onReset,
}) => {
    const BASE_URL = process.env.REACT_APP_PROXY;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [idPedidoPago, setIdPedidoPago] = useState<number | null>(null);
    const scriptRef = useRef<HTMLScriptElement | null>(null);

    const formId = `${renderKey}-form`;
    const iframeName = `${renderKey}-sink`;

    /**
     * Limpieza total de scripts, iframes y contextos previos de Niubiz.
     */
    const hardClean = () => {
        if (scriptRef.current) {
            try {
                scriptRef.current.remove();
            } catch {}
            scriptRef.current = null;
        }

        // Elimina cualquier elemento previo inyectado por Niubiz
        document
            .querySelectorAll(
                'script[src*="checkout.js"], iframe[src*="niubiz"], iframe[src*="vnforapps"], .niubiz-checkout, .btn-pay, .btn-niubiz, [data-merchantid]',
            )
            .forEach((el) => el.remove());

        const formEl = document.getElementById(formId);
        if (formEl) formEl.innerHTML = "";

        // Resetea el contexto global de Niubiz
        if ((window as any).niubizCheckout) {
            delete (window as any).niubizCheckout;
        }
    };

    /**
     * Inyección controlada del script del botón Niubiz.
     */
    const injectScript = ({
        sessionKey,
        purchaseNumber,
        merchantId,
        amount,
    }: {
        sessionKey: string;
        purchaseNumber: string;
        merchantId: string;
        amount: string;
    }) => {
        const maxAttempts = 30;
        let attempts = 0;

        const attemptInject = () => {
            const formEl = document.getElementById(formId);
            attempts++;
            console.log(
                `NiubizPayButton: attemptInject #${attempts} for formId="${formId}"`,
            );

            if (!formEl) {
                if (attempts < maxAttempts) {
                    requestAnimationFrame(attemptInject);
                } else {
                    console.error(
                        `NiubizPayButton: form element not found after ${maxAttempts} attempts.`,
                    );
                    setError(
                        "Error interno: formulario de pago no se encontró.",
                    );
                }
                return;
            }

            console.log(
                "NiubizPayButton: form element found, injecting script.",
            );

            const script = document.createElement("script");
            script.type = "text/javascript";
            script.src = `${SANDBOX_BASE}?v=${Date.now()}`;
            script.async = true;

            // Atributos dinámicos de sesión
            const buttonId = `niubiz-btn-${Date.now()}`;
            script.setAttribute("data-buttonid", buttonId);
            script.setAttribute("data-sessiontoken", sessionKey);
            script.setAttribute("data-channel", "web");
            script.setAttribute("data-merchantid", merchantId);
            script.setAttribute("data-purchasenumber", purchaseNumber);
            script.setAttribute("data-amount", amount);
            script.setAttribute("data-expirationminutes", "20");
            script.setAttribute("data-timeouturl", "about:blank");
            script.setAttribute("data-buttoncolor", "NAVY");

            formEl.appendChild(script);
            scriptRef.current = script;

            console.log("NiubizPayButton: script injected.");
        };

        // Garantiza que React haya montado el DOM
        setTimeout(() => {
            requestAnimationFrame(attemptInject);
        }, 0);
    };

    /**
     * Crea una nueva sesión Niubiz y renderiza el botón.
     */
    const createSessionAndRender = async () => {
        setLoading(true);
        setError(null);
        hardClean();

        try {
            const body: Record<string, any> = {
                id_pedidos_habitaciones:
                    tipo_pedido === "Habitación" ? idReserva : 0,

                id_pedido: tipo_pedido === "Casa" ? idReserva : 0,
                tipo_moneda: tipoMoneda,
                amount: String(amount),
                etapa_pago: tipoPago,
                is_igv: isComprobante ? "Si" : "No",
                tipo_pedido: tipo_pedido,
            };

            if (isComprobante && factData) {
                Object.assign(body, {
                    tipo_comprobante: factData.tipo_comprobante,
                    correo: factData.correo,
                    correo_copia: factData.correo_copia || "",
                    direccion: factData.direccion,
                    ubigeo: factData.ubigeo,
                    nombre_comercial: factData.nombre_comercial,
                    nombre_legal: factData.nombre_legal,
                    numero_documento_identidad:
                        factData.numero_documento_identidad,
                    tipo_documento_identidad: factData.tipo_documento_identidad,
                });
            }

            const data = await fetchAxiosNoToken({
                url: "/niubiz/session",
                method: "post",
                body,
            });

            console.log("✅ Niubiz session response:", data);

            const sessionKey = data?.sessionKey;
            const purchaseNumber = data?.purchase_number;
            const merchantId = data?.id_comercio;
            const idPago = data?.id_pedido_pago;

            if (!sessionKey || !purchaseNumber || !idPago) {
                throw new Error("Faltan datos de sesión de Niubiz.");
            }

            setIdPedidoPago(idPago);

            injectScript({
                sessionKey,
                purchaseNumber,
                merchantId,
                amount: String(amount),
            });
        } catch (e: any) {
            console.error("❌ Error creando sesión Niubiz:", e);
            setError(e?.message || "Error generando sesión Niubiz.");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Efecto principal: genera y renderiza el botón Niubiz cada vez que cambian dependencias.
     */
    useEffect(() => {
        if (!idReserva || !amount) return;
        createSessionAndRender();

        return () => {
            hardClean();
        };
    }, [
        renderKey,
        idReserva,
        amount,
        tipoPago,
        isComprobante,
        JSON.stringify(factData),
    ]);

    return (
        <div key={renderKey} className="w-full mt-3 min-h-[70px]">
            {loading && (
                <div className="flex flex-col items-center justify-center py-6 text-sm text-slate-500 animate-pulse">
                    <div className="w-5 h-5 mb-2 border-2 rounded-full animate-spin border-slate-300 border-t-transparent" />
                    Generando botón de pago...
                </div>
            )}

            {!loading && error && (
                <div className="p-2 mb-2 text-sm text-white bg-red-500 rounded">
                    {error}
                </div>
            )}

            <form
                key={formId} // fuerza recreación
                id={formId}
                action={`${BASE_URL}/niubiz/transaction/${idPedidoPago}/${id_reserva}`}
                method="post"
                target={iframeName}
            ></form>

            <iframe name={iframeName} style={{ display: "none" }} />

            {!loading && !error && (
                <button
                    type="button"
                    className="mt-3 text-sm text-blue-600 underline"
                    onClick={() => {
                        if (onReset) onReset();
                    }}
                >
                    cambiar opciones de pago
                </button>
            )}
        </div>
    );
};

export default NiubizPayButton;
